import { z } from "zod";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
/** Files (docs/specs/files.md). The rules live here so the Admin can check before uploading. */
export const FILE_MAX_BYTES = 10 * 1024 * 1024;
export const FILE_TYPES = {
    jpeg: "image/jpeg",
    png: "image/png",
    webp: "image/webp",
    pdf: "application/pdf",
    csv: "text/csv",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    /** ST-20: a short muted loop for a storefront hero (served as uploaded, ≤ 10 MB). */
    mp4: "video/mp4",
};
const images = [FILE_TYPES.jpeg, FILE_TYPES.png, FILE_TYPES.webp];
/** What each purpose accepts. SVG, HTML, executables and other archives are never accepted. */
export const FILE_PURPOSE_TYPES = {
    product_image: images,
    logo: images,
    /** ST-20 (ADR-0024 "Video"): a storefront section's background video. */
    section_video: [FILE_TYPES.mp4],
    attachment: [...images, FILE_TYPES.pdf],
    import: [FILE_TYPES.csv, FILE_TYPES.xlsx],
    /** DG-05: an eBook's PDF (private; read in the shop's reader only) and its free sample (marked by a job). */
    ebook: [FILE_TYPES.pdf],
    ebook_sample: [FILE_TYPES.pdf],
    /** Written by the server (templates, exports, error reports, PDFs; imports.md, ADR-0018); never uploadable. */
    generated: [FILE_TYPES.csv, FILE_TYPES.xlsx, FILE_TYPES.pdf],
};
export const FILE_PURPOSES = Object.keys(FILE_PURPOSE_TYPES);
export const IMAGE_TYPES = images;
/** DG-05: the most each purpose takes; everything else is `FILE_MAX_BYTES` (10 MB). */
export const EBOOK_MAX_BYTES = 200 * 1024 * 1024;
export const EBOOK_SAMPLE_MAX_BYTES = 20 * 1024 * 1024;
export function fileMaxBytes(purpose) {
    if (purpose === "ebook")
        return EBOOK_MAX_BYTES;
    if (purpose === "ebook_sample")
        return EBOOK_SAMPLE_MAX_BYTES;
    return FILE_MAX_BYTES;
}
/** Purposes a browser may upload (everything but `generated`). Non-empty: the cast keeps the tuple type for z.enum. */
export const UPLOAD_PURPOSES = FILE_PURPOSES.filter((p) => p !== "generated");
/** `POST /v1/files/uploads` */
export const fileUploadRequestSchema = z
    .object({
    purpose: z.enum(UPLOAD_PURPOSES),
    fileName: z.string().trim().min(1).max(255),
    contentType: z.string().min(1).max(127),
    sizeBytes: z.number().int().min(1).max(EBOOK_MAX_BYTES),
})
    .superRefine((value, ctx) => {
    if (value.sizeBytes > fileMaxBytes(value.purpose)) {
        ctx.addIssue({
            code: "custom",
            path: ["sizeBytes"],
            message: `${value.purpose} takes at most ${String(fileMaxBytes(value.purpose))} bytes`,
        });
    }
    const allowed = FILE_PURPOSE_TYPES[value.purpose];
    if (!allowed.includes(value.contentType)) {
        ctx.addIssue({
            code: "custom",
            path: ["contentType"],
            message: `${value.purpose} accepts ${allowed.join(", ")}`,
        });
    }
});
/** Where to PUT the bytes: send exactly these headers and exactly `sizeBytes` bytes before `expiresAt`. */
export const fileUploadResponseSchema = z.object({
    fileId: z.uuid(),
    uploadUrl: z.url(),
    method: z.literal("PUT"),
    headers: z.record(z.string(), z.string()),
    expiresAt: z.iso.datetime(),
});
export const fileStatusSchema = z.enum(["pending", "processing", "ready", "rejected"]);
export const fileSchema = z.object({
    id: z.uuid(),
    purpose: z.enum(FILE_PURPOSES),
    originalName: z.string(),
    contentType: z.string(),
    sizeBytes: z.number().int(),
    status: fileStatusSchema,
    rejectedReason: z.string().nullable(),
    createdAt: z.iso.datetime(),
    /** Signed, short-lived download URLs; only for `ready` files. */
    urls: z.object({ original: z.url(), thumb: z.url().optional(), medium: z.url().optional() }).nullable(),
});
// Endpoints -------------------------------------------------------------------------------------------------
export const requestFileUpload = defineEndpoint({
    name: "requestFileUpload",
    method: "POST",
    path: "/v1/files/uploads",
    access: { permission: "files.upload" },
    summary: "A signed PUT URL for one file (type and size are signed)",
    tag: "files",
    body: fileUploadRequestSchema,
    status: 201,
    response: fileUploadResponseSchema,
});
export const confirmFileUpload = defineEndpoint({
    name: "confirmFileUpload",
    method: "POST",
    path: "/v1/files/{id}/confirm",
    access: { permission: "files.upload" },
    summary: "Check the uploaded bytes; images then get processed",
    tag: "files",
    params: idParamsSchema,
    status: 200,
    response: fileSchema,
    errors: ["FILE_REJECTED", "CONFLICT"],
});
export const getFile = defineEndpoint({
    name: "getFile",
    method: "GET",
    path: "/v1/files/{id}",
    access: { permission: "files.view" },
    summary: "File details and short-lived download URLs",
    tag: "files",
    params: idParamsSchema,
    status: 200,
    response: fileSchema,
});
export const deleteFile = defineEndpoint({
    name: "deleteFile",
    method: "DELETE",
    path: "/v1/files/{id}",
    access: { permission: "files.upload" },
    summary: "Delete a file",
    tag: "files",
    params: idParamsSchema,
    status: 204,
});
//# sourceMappingURL=files.js.map